import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, Subject, of, combineLatest } from 'rxjs';
import { takeUntil, catchError, startWith, map, filter, take } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrandKitService, PublicKit, KitPreviewColor } from '../../../_core/services/brand-kit.service';
import {
	BrandKitColorSelectionService,
	ColorPickerColor
} from '../../../_core/services/brand-kit-color-selection.service';

/** Kit state for template - loading, error, or data */
interface KitState {
	kits: PublicKit[];
	loading: boolean;
	error: string | null;
}

/** Color option interface for display */
interface ColorOption {
	value: string;
	name?: string;
	label?: string;
}

@Component({
	selector: 'app-brand-kit-color-picker',
	standalone: true,
	imports: [
		CommonModule,
		MatProgressSpinnerModule,
		MatButtonModule,
		MatIconModule,
		MatTooltipModule
	],
	templateUrl: './brand-kit-color-picker.component.html',
	styleUrls: ['./brand-kit-color-picker.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class BrandKitColorPickerComponent implements OnInit, OnDestroy {
	@Input() packageId!: string;
	@Input() currentValue: string | null = null;
	@Output() colorSelected = new EventEmitter<string>();
	@Output() hasKitsChanged = new EventEmitter<boolean>();

	private destroy$ = new Subject<void>();

	/** Observable state for template - handles loading/error/data */
	kitState$: Observable<KitState>;
	selectedKitId$: Observable<string | null>;

	/** Observable for brand kit colors */
	brandKitColors$: Observable<ColorOption[]>;

	/** Track dropdown open state */
	isDropdownOpen = false;

	/** Cache the current selected kit ID for synchronous access */
	private currentSelectedKitId: string | null = null;

	constructor(
		private brandKitService: BrandKitService,
		private colorSelectionService: BrandKitColorSelectionService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		if (!this.packageId) {
			console.error('BrandKitColorPicker: packageId is required');
			return;
		}

		// Get selection state for this package
		this.selectedKitId$ = this.colorSelectionService.getSelectedKitId$(this.packageId);

		// Track selected kit ID for synchronous display
		this.selectedKitId$.pipe(takeUntil(this.destroy$)).subscribe(kitId => {
			this.currentSelectedKitId = kitId;
			this.cdr.markForCheck();
		});

		// Validate saved kit still exists
		this.colorSelectionService.validateSavedKit(this.packageId);

		// Get colors from selected brand kit
		this.brandKitColors$ = this.colorSelectionService.getSelectedKitColors$(this.packageId).pipe(
			map((kitColors) => {
				if (kitColors && kitColors.length > 0) {
					return kitColors.map(c => ({ value: c.value, name: c.name, label: c.name }));
				}
				return [];
			}),
			takeUntil(this.destroy$)
		);

		this.loadKits();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/** Toggle the dropdown open/closed */
	toggleDropdown(): void {
		this.isDropdownOpen = !this.isDropdownOpen;
		this.cdr.markForCheck();
	}

	/** Select a kit and close dropdown */
	selectKit(kitId: string): void {
		this.colorSelectionService.selectKit(this.packageId, kitId);
		this.isDropdownOpen = false;
		this.cdr.markForCheck();
	}

	/** Handle color selection */
	onColorClick(color: ColorOption): void {
		this.colorSelected.emit(color.value);
	}

	onRetry(): void {
		this.loadKits();
		this.cdr.markForCheck();
	}

	/** Get the display name for the currently selected kit */
	getSelectedKitName(kits: PublicKit[]): string {
		if (!this.currentSelectedKitId) {
			return kits[0]?.name || 'Brand Kit';
		}
		const kit = kits.find(k => k.id === this.currentSelectedKitId);
		return kit?.name || kits[0]?.name || 'Brand Kit';
	}

	/** Safe accessor for kit preview colors */
	getPreviewColors(kit: PublicKit): KitPreviewColor[] {
		if (!kit?.preview?.colors || !Array.isArray(kit.preview.colors)) {
			return [];
		}
		return kit.preview.colors.slice(0, 4);
	}

	/** Check if a kit has any colors */
	hasColors(kit: PublicKit): boolean {
		return kit?.preview?.colors?.length > 0;
	}

	/** Check if a color is currently selected */
	isColorSelected(color: ColorOption): boolean {
		return color.value === this.currentValue;
	}

	private loadKits(): void {
		this.kitState$ = this.brandKitService.findAllKits().pipe(
			map(kits => {
				const kitsWithColors = kits.filter(kit => this.hasColors(kit));
				this.hasKitsChanged.emit(kitsWithColors.length > 0);
				return { kits: kitsWithColors, loading: false, error: null };
			}),
			startWith({ kits: [], loading: true, error: null }),
			catchError(() => {
				this.hasKitsChanged.emit(false);
				return of({ kits: [], loading: false, error: 'Failed to load brand kits' });
			}),
			takeUntil(this.destroy$)
		);

		this.autoSelectFirstKit();
	}

	private autoSelectFirstKit(): void {
		combineLatest([
			this.kitState$.pipe(filter(state => !state.loading && !state.error)),
			this.selectedKitId$
		]).pipe(
			take(1),
			takeUntil(this.destroy$)
		).subscribe(([state, selectedKitId]) => {
			if (state.kits.length > 0 && !selectedKitId) {
				this.selectKit(state.kits[0].id);
			} else if (selectedKitId && !state.kits.find(k => k.id === selectedKitId)) {
				if (state.kits.length > 0) {
					this.selectKit(state.kits[0].id);
				}
			}
		});
	}
}
